// File: client/src/variants/power/abilities.js
//
// Pure ability + cooldown logic for Power Chess (no React). Each piece TYPE has one
// active ability with a cooldown measured in the owner's turns. Abilities are
// expressed as data + pure helpers so they can be driven by the runtime through the
// variant contract (transformLegalMoves for extra movement, afterMove for effects
// like capture-immunity), with NO engine changes.
//
// Ability model:
//   id, name, icon, piece (letter, lowercase), cooldown (owner turns), describe,
//   kind: 'move' | 'effect'
//     - 'move' abilities expand a piece's legal moves for the activation turn
//       (extraMoves(board,pos,isWhite) -> squares).
//     - 'effect' abilities apply an instantaneous/lasting effect (handled by runtime).
//
// Design decisions (documented in the rulebook) where the brief was thin:
//   - Bishop "Heal / remove a debuff": there is no debuff system, so Heal is
//     reinterpreted as resurrecting your most-recently-captured pawn onto an empty
//     square adjacent to the bishop.
//   - Knight "Leap / ignore occupied path": knights already leap, so Leap is
//     reinterpreted as letting the knight reach ANY square within Chebyshev distance
//     2 (ignoring blockers) for the turn.
//   - Rook "Fortify / immune one turn": the rook cannot be captured until the
//     owner's next turn.
//   - Queen "Blitz / two moves": the turn stays open for a second move (no moving
//     the same piece twice — enforced like UNO number cards).
//   - King "Royal Command": resets the cooldowns of all the owner's abilities.

const chebyshev = (a, b) => Math.max(Math.abs(a.row - b.row), Math.abs(a.col - b.col));

export const ABILITIES = {
  p: {
    id: 'sprint', name: 'Sprint', icon: '🏃', piece: 'p', cooldown: 3, kind: 'move',
    describe: 'This pawn may move two squares forward (both empty), from any rank.',
    extraMoves(board, pos, isWhite) {
      const dir = isWhite ? -1 : 1;
      const r1 = pos.row + dir;
      const r2 = pos.row + 2 * dir;
      if (r2 < 0 || r2 > 7) return [];
      if (board[r1] && board[r1][pos.col] === '' && board[r2][pos.col] === '') {
        return [{ row: r2, col: pos.col, type: 'normal' }];
      }
      return [];
    },
  },
  n: {
    id: 'leap', name: 'Leap', icon: '🦘', piece: 'n', cooldown: 3, kind: 'move',
    describe: 'This knight may jump to any square within 2 (ignoring blockers).',
    extraMoves(board, pos, isWhite) {
      const out = [];
      for (let r = 0; r < 8; r++) {
        for (let c = 0; c < 8; c++) {
          if (r === pos.row && c === pos.col) continue;
          if (chebyshev(pos, { row: r, col: c }) > 2) continue;
          const t = board[r][c];
          if (!t || t === '' || (t === t.toUpperCase()) !== isWhite) {
            out.push({ row: r, col: c, type: t ? 'capture' : 'normal' });
          }
        }
      }
      return out;
    },
  },
  b: {
    id: 'cleanse', name: 'Cleanse', icon: '➕', piece: 'b', cooldown: 4, kind: 'effect',
    describe: 'This bishop shrugs off harm — it cannot be captured until your next turn.',
    // effect handled by the runtime (capture-immunity, like Fortify but for a bishop)
  },
  r: {
    id: 'fortify', name: 'Fortify', icon: '🛡️', piece: 'r', cooldown: 4, kind: 'effect',
    describe: 'This rook cannot be captured until your next turn.',
    // effect handled by the runtime (capture-immunity set)
  },
  q: {
    id: 'blitz', name: 'Blitz', icon: '⚡', piece: 'q', cooldown: 5, kind: 'effect',
    describe: 'Make two moves this turn (not the same piece twice).',
    // effect handled by the runtime (keeps the turn open)
  },
  k: {
    id: 'royal_command', name: 'Royal Command', icon: '👑', piece: 'k', cooldown: 6, kind: 'effect',
    describe: 'Reset the cooldowns of all your abilities.',
    // effect handled by the runtime (cooldown reset)
  },
};

/** Ability for a board letter (any case), or null. */
export function abilityForPiece(letter) {
  if (!letter) return null;
  return ABILITIES[letter.toLowerCase()] || null;
}

// ---- cooldown state ----
// State shape: { white: {sprint:0,...}, black: {...} } where value = turns remaining
// until the ability is usable again (0 = ready).

/** Fresh cooldown state (all ready). */
export function initCooldowns() {
  const zero = () => Object.values(ABILITIES).reduce((m, a) => ((m[a.id] = 0), m), {});
  return { white: zero(), black: zero() };
}

/** Is an ability ready for a color? */
export function isReady(cooldowns, color, abilityId) {
  return (cooldowns[color][abilityId] || 0) <= 0;
}

/** Put an ability on cooldown (returns new state). */
export function trigger(cooldowns, color, abilityId) {
  const ability = Object.values(ABILITIES).find(a => a.id === abilityId);
  const cd = ability ? ability.cooldown : 0;
  return {
    ...cooldowns,
    [color]: { ...cooldowns[color], [abilityId]: cd },
  };
}

/** Tick down a color's cooldowns by one of their turns (returns new state). */
export function tick(cooldowns, color) {
  const next = { ...cooldowns[color] };
  for (const k of Object.keys(next)) next[k] = Math.max(0, next[k] - 1);
  return { ...cooldowns, [color]: next };
}

/** Reset all of a color's cooldowns to ready (Royal Command). */
export function resetAll(cooldowns, color) {
  const next = { ...cooldowns[color] };
  for (const k of Object.keys(next)) next[k] = 0;
  return { ...cooldowns, [color]: next };
}
